// src/components/tabs/GeneralStats.tsx
import React from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { TabComponentProps } from "../../interfaces/types";
import Error403Page from "../Error_backend";

// Define interfaces for the data structures
interface ServiceAppointment {
  serviceName: string;
  appointmentCount: number;
  percentage: number;
}

interface LocationAppointment {
  locationName: string;
  appointmentCount: number;
}

interface KeyMetrics {
  patientGrowth: number;
}

interface GeneralData {
  totalCompletedAppointments: number;
  totalRevenue: number;
  cancellationNoShowRate: number;
  newPatients: number;
  averageRevenuePerVisit: number;
  appointmentsByService: ServiceAppointment[];
  appointmentsByLocation: LocationAppointment[];
  keyMetrics: KeyMetrics;
}

// Color palette for the pie chart
const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884D8",
  "#82CA9D",
];

const GeneralStats: React.FC<TabComponentProps> = ({
  data,
  loading,
  error,
  dateRange,
  onRetry,
}) => {
  // Cast the data to GeneralData type
  const generalData = data as GeneralData;

  // Format data for Recharts Pie chart
  const pieChartData =
    generalData?.appointmentsByService?.map((service) => ({
      name: service.serviceName,
      value: service.appointmentCount,
      percentage: service.percentage,
    })) || [];

  // Custom tooltip formatter
  const customTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div
          className="custom-tooltip"
          style={{
            backgroundColor: "white",
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <p style={{ margin: 0, fontWeight: "bold" }}>{data.name}</p>
          <p style={{ margin: "5px 0 0 0" }}>
            Citas: <strong>{data.value}</strong>
          </p>
          <p style={{ margin: "2px 0 0 0" }}>
            Porcentaje: <strong>{data.percentage}%</strong>
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom label formatter
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    index,
  }: any) => {
    if (percent < 0.05) return null; // Don't show label for very small slices

    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        fontSize={12}
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  if (loading) {
    return (
      <div className="stats-loading">
        <p>Cargando estadísticas generales...</p>
      </div>
    );
  }

  if (error) {
    return <Error403Page />;
  }

  if (!generalData) {
    return (
      <div className="stats-no-data">
        <p>No hay datos disponibles para el rango de fechas seleccionado.</p>
      </div>
    );
  }

  return (
    <>
      <div className="stats-kpi-grid">
        {/* KPI Card 1: Citas Totales */}
        <div className="stats-kpi-card">
          <p className="stats-kpi-card__title">Citas Totales (Completadas)</p>
          <h3 className="stats-kpi-card__value">
            {generalData.totalCompletedAppointments?.toLocaleString() || 0}
          </h3>
        </div>

        {/* KPI Card 2: Ingreso Total */}
        <div className="stats-kpi-card stats-kpi-card--revenue">
          <p className="stats-kpi-card__title">Ingreso Total</p>
          <h3 className="stats-kpi-card__value">
            ${generalData.totalRevenue?.toLocaleString() || 0}
          </h3>
        </div>

        {/* KPI Card 3: Tasa de No-Show / Cancelación */}
        <div className="stats-kpi-card stats-kpi-card--cancellations">
          <p className="stats-kpi-card__title">Tasa de No-Show / Cancelación</p>
          <h3 className="stats-kpi-card__value">
            {generalData.cancellationNoShowRate?.toFixed(1) || 0}%
          </h3>
        </div>
      </div>

      <div className="stats-details-grid">
        {/* Chart Panel */}
        {/* <div className="stats-chart-panel">
          <h3>Productividad por Tipo de Servicio</h3>
          <p className="text-secondary">
            Distribución de las citas completadas por la categoría de servicio.
          </p>

          <div
            className="stats-chart-area"
            style={{ height: "300px", marginBottom: "20px" }}
          >
            {pieChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomizedLabel}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={customTooltip} />
                  <Legend
                    layout="vertical"
                    verticalAlign="middle"
                    align="right"
                    wrapperStyle={{
                      paddingLeft: "20px",
                      fontSize: "12px",
                    }}
                    formatter={(value, entry: any) => (
                      <span style={{ color: "#333", fontSize: "12px" }}>
                        {value}
                      </span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#666",
                  fontSize: "14px",
                }}
              >
                No hay datos de servicios para mostrar.
              </div>
            )}
          </div>

          <div style={{ marginTop: "var(--space-md)" }}>
            {generalData.appointmentsByService?.map(
              (service: ServiceAppointment, index: number) => (
                <div key={index} className="stats-list-item">
                  <span className="stats-list-item-label">
                    <span
                      style={{
                        display: "inline-block",
                        width: "12px",
                        height: "12px",
                        backgroundColor: COLORS[index % COLORS.length],
                        marginRight: "8px",
                        borderRadius: "2px",
                      }}
                    ></span>
                    {service.serviceName}
                  </span>
                  <span className="stats-list-item-value">
                    {service.appointmentCount} citas ({service.percentage}%)
                  </span>
                </div>
              )
            )}
          </div>
        </div> */}

        {/* List Panel */}
        <div className="stats-list-panel">
          <h3>Métricas Clave Adicionales</h3>

          <div style={{ marginBottom: "var(--space-md)" }}>
            <p
              className="stats-kpi-card__title"
              style={{ marginBottom: "var(--space-xs)" }}
            >
              Nuevos Pacientes (Período)
            </p>
            <div className="stats-list-item">
              <h4 style={{ margin: 0, color: "var(--color-accent)" }}>
                {generalData.newPatients || 0}
              </h4>
            </div>
          </div>

          <div style={{ marginBottom: "var(--space-md)" }}>
            <p
              className="stats-kpi-card__title"
              style={{ marginBottom: "var(--space-xs)" }}
            >
              Ingreso Promedio por Visita (ARPV)
            </p>
            <div className="stats-list-item">
              <h4 style={{ margin: 0 }}>
                ${generalData.averageRevenuePerVisit?.toFixed(2) || "0.00"}
              </h4>
            </div>
          </div>

          <div style={{ marginBottom: "var(--space-md)" }}>
            <p
              className="stats-kpi-card__title"
              style={{ marginBottom: "var(--space-xs)" }}
            >
              Citas por Ubicación
            </p>
            {generalData.appointmentsByLocation?.map(
              (location: LocationAppointment, index: number) => (
                <div key={index} className="stats-list-item">
                  <span className="stats-list-item-label">
                    {location.locationName}
                  </span>
                  <span className="stats-list-item-value">
                    {location.appointmentCount} citas
                  </span>
                </div>
              ),
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default GeneralStats;
